import { NavNode, NavEdge } from '../types/map';

export interface PathResult {
  nodes: NavNode[];
  edges: NavEdge[];
  coordinates: [number, number][]; // [lng, lat] for GeoJSON
  totalDistance: number; // meters
}

// ─── Spatial Index for Edge Lookups ───────────────────────────
// Grid-based bucketing: edges are binned into ~100m cells.
// snapToNearestEdge and findNearestEdge search only nearby cells
// instead of iterating ALL edges — O(1) amortized vs O(E×G).

const GRID_CELL_SIZE = 0.001; // ~111m in latitude, ~80-100m in longitude

interface EdgeGridCell {
  edges: NavEdge[];
}

// Grid caches are stored in getEdgeGrid()

function getGridKey(lat: number, lng: number): string {
  const gLat = Math.floor(lat / GRID_CELL_SIZE);
  const gLng = Math.floor(lng / GRID_CELL_SIZE);
  return `${gLat},${gLng}`;
}

function buildEdgeGrid(edges: NavEdge[]): Map<string, EdgeGridCell> {
  const grid = new Map<string, EdgeGridCell>();
  for (const edge of edges) {
    // Add edge to every cell its geometry passes through
    const visited = new Set<string>();
    for (const [lng, lat] of edge.geometry) {
      const key = getGridKey(lat, lng);
      if (visited.has(key)) continue;
      visited.add(key);
      let cell = grid.get(key);
      if (!cell) {
        cell = { edges: [] };
        grid.set(key, cell);
      }
      cell.edges.push(edge);
    }
  }
  return grid;
}

// Cache grids by edge count (full edges vs filtered)
let edgeGridA: { count: number; grid: Map<string, EdgeGridCell> } | null = null;
let edgeGridB: { count: number; grid: Map<string, EdgeGridCell> } | null = null;

function getEdgeGrid(edges: NavEdge[]): Map<string, EdgeGridCell> {
  const count = edges.length;
  if (edgeGridA && edgeGridA.count === count) return edgeGridA.grid;
  if (edgeGridB && edgeGridB.count === count) return edgeGridB.grid;
  const grid = buildEdgeGrid(edges);
  // Evict oldest
  edgeGridB = edgeGridA;
  edgeGridA = { count, grid };
  return grid;
}

/** Get candidate edges near a point by checking a 5×5 neighborhood of grid cells (~500m) */
function getNearbyEdges(lat: number, lng: number, edges: NavEdge[]): NavEdge[] {
  const grid = getEdgeGrid(edges);
  const baseLat = Math.floor(lat / GRID_CELL_SIZE);
  const baseLng = Math.floor(lng / GRID_CELL_SIZE);
  const seen = new Set<string>();
  const result: NavEdge[] = [];

  for (let dLat = -2; dLat <= 2; dLat++) {
    for (let dLng = -2; dLng <= 2; dLng++) {
      const key = `${baseLat + dLat},${baseLng + dLng}`;
      const cell = grid.get(key);
      if (!cell) continue;
      for (const edge of cell.edges) {
        if (!seen.has(edge.id)) {
          seen.add(edge.id);
          result.push(edge);
        }
      }
    }
  }
  return result;
}

/**
 * Binary min-heap for Dijkstra priority queue — O(log n) insert/extract
 * instead of O(n) linear scan.
 */
class MinHeap {
  private heap: { nodeId: string; dist: number }[] = [];

  get length() { return this.heap.length; }

  push(item: { nodeId: string; dist: number }) {
    this.heap.push(item);
    this._bubbleUp(this.heap.length - 1);
  }

  pop(): { nodeId: string; dist: number } | undefined {
    const top = this.heap[0];
    const last = this.heap.pop();
    if (this.heap.length > 0 && last) {
      this.heap[0] = last;
      this._sinkDown(0);
    }
    return top;
  }

  private _bubbleUp(i: number) {
    const h = this.heap;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (h[i].dist >= h[parent].dist) break;
      [h[i], h[parent]] = [h[parent], h[i]];
      i = parent;
    }
  }

  private _sinkDown(i: number) {
    const h = this.heap;
    const len = h.length;
    while (true) {
      let smallest = i;
      const left = 2 * i + 1;
      const right = 2 * i + 2;
      if (left < len && h[left].dist < h[smallest].dist) smallest = left;
      if (right < len && h[right].dist < h[smallest].dist) smallest = right;
      if (smallest === i) break;
      [h[i], h[smallest]] = [h[smallest], h[i]];
      i = smallest;
    }
  }
}

/**
 * Find shortest path from startNodeId to endNodeId using Dijkstra,
 * constrained by maxDistance (meters). Returns null if unreachable within range.
 */
export function findPath(
  startNodeId: string,
  endNodeId: string,
  nodes: NavNode[],
  edges: NavEdge[],
  maxDistance: number,
): PathResult | null {
  const nodeMap = new Map<string, NavNode>();
  for (const n of nodes) nodeMap.set(n.id, n);

  const edgeMap = new Map<string, NavEdge>();
  for (const e of edges) edgeMap.set(e.id, e);

  // Dijkstra
  const dist = new Map<string, number>();
  const prev = new Map<string, { nodeId: string; edgeId: string } | null>();
  const visited = new Set<string>();

  dist.set(startNodeId, 0);
  prev.set(startNodeId, null);

  // Binary min-heap priority queue — O(n log n) Dijkstra
  const queue = new MinHeap();
  queue.push({ nodeId: startNodeId, dist: 0 });

  while (queue.length > 0) {
    const current = queue.pop()!;

    if (visited.has(current.nodeId)) continue;
    visited.add(current.nodeId);

    if (current.nodeId === endNodeId) break;
    if (current.dist > maxDistance) continue;

    const node = nodeMap.get(current.nodeId);
    if (!node) continue;

    for (const edgeId of node.edges) {
      const edge = edgeMap.get(edgeId);
      if (!edge) continue;

      const neighborId = edge.from === current.nodeId ? edge.to : edge.from;

      const newDist = current.dist + edge.length;
      if (newDist > maxDistance) continue;

      const prevDist = dist.get(neighborId);
      if (prevDist === undefined || newDist < prevDist) {
        dist.set(neighborId, newDist);
        prev.set(neighborId, { nodeId: current.nodeId, edgeId });
        queue.push({ nodeId: neighborId, dist: newDist });
      }
    }
  }

  // Check if destination was reached
  if (!dist.has(endNodeId)) return null;

  // Reconstruct path
  const pathNodes: NavNode[] = [];
  const pathEdges: NavEdge[] = [];
  let currentId: string | null = endNodeId;

  while (currentId !== null) {
    const node = nodeMap.get(currentId);
    if (node) pathNodes.unshift(node);

    const entry = prev.get(currentId);
    if (entry) {
      const edge = edgeMap.get(entry.edgeId);
      if (edge) pathEdges.unshift(edge);
      currentId = entry.nodeId;
    } else {
      currentId = null;
    }
  }

  // Build coordinate array from edge geometries (in correct order)
  const coordinates: [number, number][] = [];
  for (let i = 0; i < pathEdges.length; i++) {
    const edge = pathEdges[i];
    const fromNode = pathNodes[i];

    // Edge geometry may need reversing if we're traversing it backwards
    let geom = edge.geometry;
    if (edge.from !== fromNode.id) {
      geom = [...geom].reverse();
    }

    // Skip first point if not the first edge (avoid duplicates at junctions)
    const startIdx = i === 0 ? 0 : 1;
    for (let j = startIdx; j < geom.length; j++) {
      coordinates.push(geom[j]);
    }
  }

  return {
    nodes: pathNodes,
    edges: pathEdges,
    coordinates,
    totalDistance: dist.get(endNodeId)!,
  };
}

/**
 * Find the nearest navigation node to a given lat/lng coordinate.
 */
export function findNearestNode(
  lat: number,
  lng: number,
  nodes: NavNode[],
): NavNode | null {
  if (nodes.length === 0) return null;

  let best = nodes[0];
  let bestDist = Infinity;
  for (const node of nodes) {
    const d = (node.lat - lat) ** 2 + (node.lng - lng) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = node;
    }
  }
  return best;
}

/**
 * Find the N nearest navigation nodes to a given lat/lng coordinate.
 */
export function findNearestNodes(
  lat: number,
  lng: number,
  nodes: NavNode[],
  count: number,
): NavNode[] {
  if (nodes.length === 0) return [];

  const withDist = nodes.map(n => ({
    node: n,
    dist: (n.lat - lat) ** 2 + (n.lng - lng) ** 2,
  }));
  withDist.sort((a, b) => a.dist - b.dist);
  return withDist.slice(0, count).map(w => w.node);
}

interface SnapPoint {
  edge: NavEdge;
  point: [number, number]; // [lng, lat]
  distAlongEdge: number; // meters from edge.from
  nearerNodeId: string; // which end is closer on the graph
  distFromNearerNode: number; // meters from the nearer node to the snap point
}

/**
 * Snap a lat/lng to the closest point on any edge in the given edge list.
 * Returns the on-edge lat/lng and the distance in meters from the click to
 * the snap point. Caller can reject the snap if the distance is too large.
 * Public wrapper around snapToNearestEdge for callers (like Surveillance
 * Van placement) that need to pin an origin on a road without caring about
 * which graph node it's nearest to.
 */
export function snapPointToEdges(
  lat: number,
  lng: number,
  edges: NavEdge[],
): { lat: number; lng: number; distanceM: number } | null {
  const snap = snapToNearestEdge(lat, lng, edges);
  if (!snap) return null;
  const [snapLng, snapLat] = snap.point;
  return {
    lat: snapLat,
    lng: snapLng,
    distanceM: haversine(lat, lng, snapLat, snapLng),
  };
}

/**
 * Find the closest point on any edge geometry to a given lat/lng.
 */
function snapToNearestEdge(
  lat: number,
  lng: number,
  edges: NavEdge[],
): SnapPoint | null {
  // Use spatial index to check only nearby edges instead of all edges
  const nearby = getNearbyEdges(lat, lng, edges);
  // Fallback to full scan if spatial index returns nothing (edge case: point far from any road)
  const candidates = nearby.length > 0 ? nearby : edges;

  let bestDist = Infinity;
  let bestSnap: SnapPoint | null = null;

  for (const edge of candidates) {
    const geom = edge.geometry;
    for (let i = 0; i < geom.length - 1; i++) {
      const [ax, ay] = geom[i];     // lng, lat
      const [bx, by] = geom[i + 1];

      const dx = bx - ax;
      const dy = by - ay;
      const lenSq = dx * dx + dy * dy;
      if (lenSq === 0) continue;

      let t = ((lng - ax) * dx + (lat - ay) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));

      const px = ax + t * dx;
      const py = ay + t * dy;
      const d = (lng - px) ** 2 + (lat - py) ** 2;

      if (d < bestDist) {
        bestDist = d;

        let distFromStart = 0;
        for (let j = 0; j < i; j++) {
          const [x1, y1] = geom[j];
          const [x2, y2] = geom[j + 1];
          distFromStart += haversine(y1, x1, y2, x2);
        }
        distFromStart += haversine(ay, ax, py, px);

        const distFromEnd = edge.length - distFromStart;
        const nearerIsFrom = distFromStart <= distFromEnd;

        bestSnap = {
          edge,
          point: [px, py],
          distAlongEdge: distFromStart,
          nearerNodeId: nearerIsFrom ? edge.from : edge.to,
          distFromNearerNode: nearerIsFrom ? distFromStart : distFromEnd,
        };
      }
    }
  }

  return bestSnap;
}

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Find a path from startNodeId to any point near (lat, lng) on the road network,
 * snapping to the closest point along any edge. Returns partial edge geometry
 * so the path can end mid-street, not just at intersections.
 */
export function findPathToPoint(
  startNodeId: string,
  targetLat: number,
  targetLng: number,
  nodes: NavNode[],
  edges: NavEdge[],
  maxDistance: number,
): PathResult | null {
  const snap = snapToNearestEdge(targetLat, targetLng, edges);
  if (!snap) return null;

  const { edge: snapEdge, distAlongEdge } = snap;

  // Try paths to both ends of the snapped edge and pick the shorter total
  const endNodes = [snapEdge.from, snapEdge.to];
  let bestResult: PathResult | null = null;
  let bestTotalDist = Infinity;

  for (const endNodeId of endNodes) {
    const distEndToSnap = endNodeId === snapEdge.from ? distAlongEdge : (snapEdge.length - distAlongEdge);

    if (distEndToSnap > maxDistance) continue;

    const pathToNode = findPath(startNodeId, endNodeId, nodes, edges, maxDistance - distEndToSnap);
    if (!pathToNode) continue;

    const totalDist = pathToNode.totalDistance + distEndToSnap;
    if (totalDist > maxDistance) continue;
    if (totalDist >= bestTotalDist) continue;

    let partialGeom: [number, number][];
    if (endNodeId === snapEdge.from) {
      partialGeom = getPartialGeometry(snapEdge.geometry, distAlongEdge, true);
    } else {
      partialGeom = getPartialGeometry([...snapEdge.geometry].reverse(), snapEdge.length - distAlongEdge, true);
    }

    // When startNodeId === endNodeId, findPath returns empty coordinates.
    // In that case, use the full partialGeom (which starts at the node position).
    const combinedCoords = pathToNode.coordinates.length > 0
      ? [...pathToNode.coordinates, ...partialGeom.slice(1)]
      : partialGeom;

    bestResult = {
      nodes: pathToNode.nodes,
      edges: [...pathToNode.edges],
      coordinates: combinedCoords,
      totalDistance: totalDist,
    };
    bestTotalDist = totalDist;
  }

  return bestResult;
}

/**
 * Get geometry from the start of a coordinate array up to `targetDist` meters.
 */
function getPartialGeometry(
  geom: [number, number][],
  targetDist: number,
  _forward: boolean,
): [number, number][] {
  const result: [number, number][] = [geom[0]];
  let accumulated = 0;

  for (let i = 1; i < geom.length; i++) {
    const [x1, y1] = geom[i - 1];
    const [x2, y2] = geom[i];
    const segDist = haversine(y1, x1, y2, x2);

    if (accumulated + segDist >= targetDist) {
      // Interpolate along this segment
      const remaining = targetDist - accumulated;
      const t = segDist > 0 ? remaining / segDist : 0;
      const px = x1 + (x2 - x1) * t;
      const py = y1 + (y2 - y1) * t;
      result.push([px, py]);
      return result;
    }

    accumulated += segDist;
    result.push(geom[i]);
  }

  return result;
}

/**
 * Find a path between two arbitrary points on the road network.
 * Both start and end are snapped to the nearest edge, so the path
 * begins and ends at exact street positions — not just intersection nodes.
 * This is the primary pathfinding function for player movement.
 */
export function findPathBetweenPoints(
  startLat: number,
  startLng: number,
  targetLat: number,
  targetLng: number,
  nodes: NavNode[],
  edges: NavEdge[],
  maxDistance: number,
): PathResult | null {
  const startSnap = snapToNearestEdge(startLat, startLng, edges);
  const endSnap = snapToNearestEdge(targetLat, targetLng, edges);
  if (!startSnap || !endSnap) return null;

  // Special case: both points snap to the same edge
  if (startSnap.edge.id === endSnap.edge.id) {
    const dist = Math.abs(startSnap.distAlongEdge - endSnap.distAlongEdge);
    if (dist > maxDistance) return null;

    // Build geometry along the edge between the two snap points
    const geom = startSnap.edge.geometry;
    let coords: [number, number][];
    if (startSnap.distAlongEdge <= endSnap.distAlongEdge) {
      // Forward along edge
      const fullPartial = getPartialGeometry(geom, endSnap.distAlongEdge, true);
      // Trim the beginning up to startSnap
      coords = trimGeometryStart(fullPartial, startSnap.distAlongEdge);
    } else {
      // Backward along edge
      const reversed = [...geom].reverse();
      const startFromEnd = startSnap.edge.length - startSnap.distAlongEdge;
      const endFromEnd = startSnap.edge.length - endSnap.distAlongEdge;
      const fullPartial = getPartialGeometry(reversed, endFromEnd, true);
      coords = trimGeometryStart(fullPartial, startFromEnd);
    }
    if (coords.length < 2) coords = [startSnap.point, endSnap.point];

    // Synthesize a "virtual sub-edge" that inherits the parent's
    // classification / signalWeight / zone / name but carries the actual
    // traveled distance as its length. Without this, downstream systems
    // (signal.ts, exposure calc, detection) would see an empty edges array
    // and treat a real on-road move as a zero-terrain move — which makes
    // short same-edge moves register as SIGNAL DARK even in loud zones.
    const parent = startSnap.edge;
    const subEdge: NavEdge = {
      ...parent,
      id: `${parent.id}-sub-${startSnap.distAlongEdge.toFixed(1)}-${endSnap.distAlongEdge.toFixed(1)}`,
      from: parent.from,
      to: parent.to,
      length: dist,
      geometry: coords,
    };

    return {
      nodes: [],
      edges: [subEdge],
      coordinates: coords,
      totalDistance: dist,
    };
  }

  // General case: try all 4 combinations of start-edge-endpoint → end-edge-endpoint
  const startEndpoints = [startSnap.edge.from, startSnap.edge.to];
  const endEndpoints = [endSnap.edge.from, endSnap.edge.to];

  let bestResult: PathResult | null = null;
  let bestTotalDist = Infinity;

  for (const startNodeId of startEndpoints) {
    const distStartToNode = startNodeId === startSnap.edge.from
      ? startSnap.distAlongEdge
      : (startSnap.edge.length - startSnap.distAlongEdge);

    if (distStartToNode > maxDistance) continue;

    for (const endNodeId of endEndpoints) {
      const distEndNodeToSnap = endNodeId === endSnap.edge.from
        ? endSnap.distAlongEdge
        : (endSnap.edge.length - endSnap.distAlongEdge);

      const budget = maxDistance - distStartToNode - distEndNodeToSnap;
      if (budget < 0) continue;

      // Route through the graph between the two intersection nodes
      let graphPath: PathResult | null;
      if (startNodeId === endNodeId) {
        // Same node — no graph traversal needed
        graphPath = { nodes: [], edges: [], coordinates: [], totalDistance: 0 };
        const node = nodes.find(n => n.id === startNodeId);
        if (node) graphPath.nodes = [node];
      } else {
        graphPath = findPath(startNodeId, endNodeId, nodes, edges, budget);
      }
      if (!graphPath) continue;

      const totalDist = distStartToNode + graphPath.totalDistance + distEndNodeToSnap;
      if (totalDist > maxDistance) continue;
      if (totalDist >= bestTotalDist) continue;

      // Build start partial geometry: from snap point to startNode
      let startGeom: [number, number][];
      if (startNodeId === startSnap.edge.from) {
        // Snap point is distAlongEdge from 'from' — go backward to 'from'
        const reversed = [...startSnap.edge.geometry].reverse();
        startGeom = getPartialGeometry(reversed, startSnap.edge.length - startSnap.distAlongEdge, true);
        startGeom.reverse(); // Now goes from snap point → from
        // Actually we want snap→from, so reverse the reversed partial
        // getPartialGeometry on reversed gives from-end to snap, reversed gives snap to from-end... let me just do it simply
        startGeom = getPartialGeometry(startSnap.edge.geometry, startSnap.distAlongEdge, true);
        startGeom.reverse(); // from snap point toward 'from' node
      } else {
        // Snap point to 'to' node — go forward from snap
        startGeom = getPartialGeometryFromPoint(
          startSnap.edge.geometry, startSnap.distAlongEdge, startSnap.edge.length
        );
      }

      // Build end partial geometry: from endNode to snap point
      let endGeom: [number, number][];
      if (endNodeId === endSnap.edge.from) {
        // From 'from' node to snap point — forward along edge
        endGeom = getPartialGeometry(endSnap.edge.geometry, endSnap.distAlongEdge, true);
      } else {
        // From 'to' node to snap point — backward along edge
        const reversed = [...endSnap.edge.geometry].reverse();
        endGeom = getPartialGeometry(reversed, endSnap.edge.length - endSnap.distAlongEdge, true);
      }

      // Combine: startGeom + graphPath.coordinates + endGeom
      const combined: [number, number][] = [];

      // Add start partial (skip nothing for first segment)
      for (const c of startGeom) combined.push(c);

      // Add graph path (skip first point to avoid duplicate at junction)
      for (let i = (combined.length > 0 ? 1 : 0); i < graphPath.coordinates.length; i++) {
        combined.push(graphPath.coordinates[i]);
      }

      // Add end partial (skip first point to avoid duplicate at junction)
      for (let i = (combined.length > 0 ? 1 : 0); i < endGeom.length; i++) {
        combined.push(endGeom[i]);
      }

      if (combined.length < 2) continue;

      bestResult = {
        nodes: graphPath.nodes,
        edges: graphPath.edges,
        coordinates: combined,
        totalDistance: totalDist,
      };
      bestTotalDist = totalDist;
    }
  }

  return bestResult;
}

/**
 * Get geometry from a point at `startDist` meters along a geometry to the end.
 */
function getPartialGeometryFromPoint(
  geom: [number, number][],
  startDist: number,
  _totalLength: number,
): [number, number][] {
  let accumulated = 0;

  for (let i = 1; i < geom.length; i++) {
    const [x1, y1] = geom[i - 1];
    const [x2, y2] = geom[i];
    const segDist = haversine(y1, x1, y2, x2);

    if (accumulated + segDist >= startDist) {
      // Start point is on this segment
      const remaining = startDist - accumulated;
      const t = segDist > 0 ? remaining / segDist : 0;
      const px = x1 + (x2 - x1) * t;
      const py = y1 + (y2 - y1) * t;

      const result: [number, number][] = [[px, py]];
      for (let j = i; j < geom.length; j++) {
        result.push(geom[j]);
      }
      return result;
    }

    accumulated += segDist;
  }

  // startDist is at or past the end
  return [geom[geom.length - 1]];
}

/**
 * Trim the beginning of a geometry, removing the first `trimDist` meters.
 */
function trimGeometryStart(
  geom: [number, number][],
  trimDist: number,
): [number, number][] {
  if (trimDist <= 0) return geom;
  return getPartialGeometryFromPoint(geom, trimDist, 0);
}

/**
 * Get all reachable nodes within maxDistance from startNodeId.
 * Returns a map of nodeId → distance.
 */
export function getReachableNodes(
  startNodeId: string,
  nodes: NavNode[],
  edges: NavEdge[],
  maxDistance: number,
): Map<string, number> {
  const nodeMap = new Map<string, NavNode>();
  for (const n of nodes) nodeMap.set(n.id, n);

  const edgeMap = new Map<string, NavEdge>();
  for (const e of edges) edgeMap.set(e.id, e);

  const dist = new Map<string, number>();
  const visited = new Set<string>();
  dist.set(startNodeId, 0);

  const queue = new MinHeap();
  queue.push({ nodeId: startNodeId, dist: 0 });

  while (queue.length > 0) {
    const current = queue.pop()!;

    if (visited.has(current.nodeId)) continue;
    visited.add(current.nodeId);

    const node = nodeMap.get(current.nodeId);
    if (!node) continue;

    for (const edgeId of node.edges) {
      const edge = edgeMap.get(edgeId);
      if (!edge) continue;

      const neighborId = edge.from === current.nodeId ? edge.to : edge.from;

      const newDist = current.dist + edge.length;
      if (newDist > maxDistance) continue;

      const prevDist = dist.get(neighborId);
      if (prevDist === undefined || newDist < prevDist) {
        dist.set(neighborId, newDist);
        queue.push({ nodeId: neighborId, dist: newDist });
      }
    }
  }

  return dist;
}

/**
 * Create a straight-line path between two points, ignoring roads/buildings.
 * Used by the Shortcut card to let the player cut through terrain.
 * Returns a PathResult with no edges (so signal calculation = 0).
 */
export function createStraightLinePath(
  startLat: number,
  startLng: number,
  targetLat: number,
  targetLng: number,
  maxDistance: number,
  nodes: NavNode[],
  edges?: NavEdge[],
): PathResult | null {
  // Calculate haversine distance
  const R = 6371000;
  const dLat = ((targetLat - startLat) * Math.PI) / 180;
  const dLng = ((targetLng - startLng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((startLat * Math.PI) / 180) *
    Math.cos((targetLat * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  if (dist > maxDistance) return null;

  // If edges provided, block shortcuts that land on the same road as the start
  if (edges && edges.length > 0) {
    const startEdge = findNearestEdge(startLat, startLng, edges);
    const targetEdge = findNearestEdge(targetLat, targetLng, edges);
    if (startEdge && targetEdge && startEdge.id === targetEdge.id) {
      return null; // can't shortcut to the same road you're on
    }
  }

  // Find the nearest node to the destination (for graph anchor)
  const nearestNode = findNearestNode(targetLat, targetLng, nodes);

  return {
    nodes: nearestNode ? [nearestNode] : [],
    edges: [], // no edges = no signal generated from path
    coordinates: [
      [startLng, startLat],
      [targetLng, targetLat],
    ],
    totalDistance: dist,
  };
}

/** Find the nearest edge to a point (for same-road detection) */
function findNearestEdge(lat: number, lng: number, edges: NavEdge[]): NavEdge | null {
  // Use spatial index to check only nearby edges
  const nearby = getNearbyEdges(lat, lng, edges);
  const candidates = nearby.length > 0 ? nearby : edges;

  let bestDist = Infinity;
  let bestEdge: NavEdge | null = null;
  for (const edge of candidates) {
    const geom = edge.geometry;
    for (let i = 0; i < geom.length - 1; i++) {
      const [ax, ay] = geom[i];
      const [bx, by] = geom[i + 1];
      const dx = bx - ax;
      const dy = by - ay;
      const lenSq = dx * dx + dy * dy;
      if (lenSq === 0) continue;
      let t = ((lng - ax) * dx + (lat - ay) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
      const px = ax + t * dx;
      const py = ay + t * dy;
      const d = (lng - px) ** 2 + (lat - py) ** 2;
      if (d < bestDist) {
        bestDist = d;
        bestEdge = edge;
      }
    }
  }
  return bestEdge;
}
